import React, { useRef, useState } from "react";
import { api, formatError } from "../../api";
import { Upload, UserPlus, X as IconX, Sparkles, FileText, Loader2 } from "lucide-react";

/**
 * "Add Candidate Manually" modal.
 *
 * Two-step UX:
 *   1) Pick a resume (PDF/DOC/DOCX, ≤10 MB). Optional "Auto-fill from resume"
 *      hits /api/postings/{pid}/applicants/manual/parse, which runs the LLM
 *      parser and returns name/email/phone/role/company/skills.
 *   2) Recruiter reviews & edits, then submits. Backend creates a sourced
 *      candidate + resume + application (status=screening) and the list
 *      refreshes — Schedule becomes immediately clickable on the new row.
 */
export default function AddCandidateModal({ posting, onClose, onAdded }) {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [autoFilled, setAutoFilled] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    current_role: "",
    current_company: "",
    source: "manual",
    send_invite: false,
  });
  const fileRef = useRef(null);

  const pid = posting?.id;
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      setErr("File is over 10 MB. Please pick a smaller resume.");
      return;
    }
    const ok = [".pdf", ".doc", ".docx"].some((ext) =>
      f.name.toLowerCase().endsWith(ext)
    );
    if (!ok) {
      setErr("Only PDF, DOC, or DOCX files are supported.");
      return;
    }
    setErr("");
    setFile(f);
    setAutoFilled(false);
  };

  const autoFill = async () => {
    if (!file) return;
    setErr("");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/postings/${pid}/applicants/manual/parse`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const p = r.data?.parsed || {};
      setForm((f) => ({
        ...f,
        name: f.name || p.name || "",
        email: f.email || p.email || "",
        phone: f.phone || p.phone || "",
        current_role: f.current_role || p.current_role || "",
        current_company: f.current_company || p.current_company || "",
      }));
      setAutoFilled(true);
      if (r.data?.warning) {
        setErr(r.data.warning);
      }
    } catch (e2) {
      setErr(formatError(e2) || "Could not auto-parse the resume.");
    } finally {
      setParsing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!file) return setErr("Please attach a resume.");
    if (!form.name.trim()) return setErr("Candidate name is required.");
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) return setErr("Please enter a valid email.");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim().toLowerCase());
      if (form.phone) fd.append("phone", form.phone.trim());
      if (form.current_role) fd.append("current_role", form.current_role.trim());
      if (form.current_company) fd.append("current_company", form.current_company.trim());
      fd.append("source", form.source || "manual");
      fd.append("send_invite", form.send_invite ? "true" : "false");
      const r = await api.post(
        `/postings/${pid}/applicants/manual`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      onAdded?.(r.data?.application);
    } catch (e2) {
      setErr(formatError(e2) || "Could not add the candidate.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="add-candidate-modal"
    >
      <div className="bg-white rounded-md w-full max-w-2xl shadow-card max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-rule">
          <div>
            <div className="eyebrow flex items-center gap-2">
              <UserPlus size={12} /> Source a candidate
            </div>
            <h3 className="font-display font-bold text-xl mt-0.5 truncate">
              Add candidate to {posting?.title || "this role"}
            </h3>
          </div>
          <button
            className="btn-ghost"
            onClick={onClose}
            aria-label="Close"
            data-testid="add-candidate-close"
          >
            <IconX size={14} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-5">
          {/* 1) Resume upload */}
          <div>
            <label className="eyebrow block mb-1.5">Step 1 · Resume (PDF, DOC or DOCX, max 10 MB)</label>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={onPickFile}
                className="hidden"
                data-testid="add-candidate-file-input"
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fileRef.current?.click()}
                data-testid="add-candidate-pick-file"
              >
                <Upload size={12} /> {file ? "Replace file" : "Choose resume"}
              </button>
              {file && (
                <span className="inline-flex items-center gap-1.5 text-sm text-muted truncate max-w-[220px]">
                  <FileText size={12} /> {file.name}
                </span>
              )}
              {file && (
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={autoFill}
                  disabled={parsing}
                  data-testid="add-candidate-autofill"
                >
                  {parsing ? (
                    <><Loader2 size={12} className="animate-spin" /> Parsing…</>
                  ) : (
                    <><Sparkles size={12} /> Auto-fill from resume</>
                  )}
                </button>
              )}
            </div>
            {autoFilled && (
              <div className="text-xs text-signal mt-1.5" data-testid="add-candidate-autofill-ok">
                <Sparkles size={10} className="inline" /> Pre-filled from resume — please review.
              </div>
            )}
          </div>

          {/* 2) Candidate details */}
          <div>
            <label className="eyebrow block mb-1.5">Step 2 · Candidate details</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted">Full name <span className="text-red-500">*</span></label>
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  required
                  data-testid="add-candidate-name"
                />
              </div>
              <div>
                <label className="text-xs text-muted">Email <span className="text-red-500">*</span></label>
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  required
                  data-testid="add-candidate-email"
                />
              </div>
              <div>
                <label className="text-xs text-muted">Phone</label>
                <input
                  className="input"
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  data-testid="add-candidate-phone"
                />
              </div>
              <div>
                <label className="text-xs text-muted">Current role</label>
                <input
                  className="input"
                  value={form.current_role}
                  onChange={(e) => setField("current_role", e.target.value)}
                  data-testid="add-candidate-current-role"
                />
              </div>
              <div>
                <label className="text-xs text-muted">Current company</label>
                <input
                  className="input"
                  value={form.current_company}
                  onChange={(e) => setField("current_company", e.target.value)}
                  data-testid="add-candidate-current-company"
                />
              </div>
              <div>
                <label className="text-xs text-muted">Source</label>
                <select
                  className="input"
                  value={form.source}
                  onChange={(e) => setField("source", e.target.value)}
                  data-testid="add-candidate-source"
                >
                  <option value="manual">Manual upload</option>
                  <option value="referral">Referral</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="walkin">Walk-in</option>
                  <option value="agency">Agency</option>
                  <option value="event">Event / Career fair</option>
                </select>
              </div>
            </div>

            <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.send_invite}
                onChange={(e) => setField("send_invite", e.target.checked)}
                data-testid="add-candidate-send-invite"
              />
              Email the candidate an invite to claim their record on SkillConnect
            </label>
          </div>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="add-candidate-error">
              {err}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-rule">
            <button type="button" className="btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting}
              data-testid="add-candidate-submit"
            >
              {submitting ? (
                <><Loader2 size={12} className="animate-spin" /> Adding…</>
              ) : (
                <><UserPlus size={12} /> Add to posting</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
